import React, { useMemo } from 'react';
import { X, RotateCcw, Loader2 } from 'lucide-react';

// Paragraph-level LCS diff of two markdown bodies → side-by-side view.
const splitBlocks = (body) => (body || '').replace(/\r\n/g, '\n').split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);

export const diffBlocks = (a, b) => {
  const n = a.length; const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) for (let j = m - 1; j >= 0; j -= 1) dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const rows = []; let i = 0; let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { rows.push({ type: 'same', left: a[i], right: b[j] }); i += 1; j += 1; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { rows.push({ type: 'removed', left: a[i], right: null }); i += 1; }
    else { rows.push({ type: 'added', left: null, right: b[j] }); j += 1; }
  }
  while (i < n) { rows.push({ type: 'removed', left: a[i++], right: null }); }
  while (j < m) { rows.push({ type: 'added', left: null, right: b[j++] }); }
  // pair adjacent removed/added into "changed" rows for a tighter side-by-side
  const out = [];
  for (let k = 0; k < rows.length; k += 1) {
    if (rows[k].type === 'removed' && rows[k + 1]?.type === 'added') { out.push({ type: 'changed', left: rows[k].left, right: rows[k + 1].right }); k += 1; }
    else out.push(rows[k]);
  }
  return out;
};

const Cell = ({ text, tone }) => (
  <div className={`px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap break-words font-mono border-b border-slate-100 ${tone}`}>{text ?? ''}</div>
);

export const LegalVersionDiff = ({ left, right, onClose, onRestore, busy, hideUnchanged = true }) => {
  const rows = useMemo(() => diffBlocks(splitBlocks(left.body), splitBlocks(right.body)), [left.body, right.body]);
  const changes = rows.filter((r) => r.type !== 'same').length;
  const visible = hideUnchanged ? rows.filter((r) => r.type !== 'same') : rows;

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/60 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-6xl max-h-[90vh] rounded-lg shadow-xl flex flex-col" data-testid="legal-diff-modal" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Compare versions</h3>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="legal-diff-summary">
              <span className="font-semibold text-red-700">v{left.version}</span> (effective {left.effective_date}) → <span className="font-semibold text-emerald-700">v{right.version}</span> (effective {right.effective_date}, live) · {changes} changed paragraph{changes === 1 ? '' : 's'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-slate-500" aria-label="Close" data-testid="legal-diff-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 text-[11px] font-bold uppercase tracking-widest text-slate-500 border-b border-slate-200">
          <div className="px-3 py-2 bg-red-50/60">v{left.version} — archived</div>
          <div className="px-3 py-2 bg-emerald-50/60">v{right.version} — live</div>
        </div>
        <div className="overflow-auto flex-1" data-testid="legal-diff-rows">
          {visible.length === 0 && <p className="p-6 text-sm text-slate-500 text-center" data-testid="legal-diff-identical">These versions have identical text (only metadata differs).</p>}
          {visible.map((r, idx) => (
            <div key={idx} className="grid grid-cols-2" data-testid={`legal-diff-row-${r.type}`}>
              <Cell text={r.left} tone={r.type === 'same' ? 'text-slate-600' : r.left ? 'bg-red-50 text-red-900' : 'bg-slate-50'} />
              <Cell text={r.right} tone={r.type === 'same' ? 'text-slate-600' : r.right ? 'bg-emerald-50 text-emerald-900' : 'bg-slate-50'} />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 p-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100" data-testid="legal-diff-cancel">Close</button>
          <button type="button" onClick={() => onRestore(left.version)} disabled={!!busy} data-testid="legal-diff-restore"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#00247D] disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Restore v{left.version} as v{right.version + 1}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalVersionDiff;
